import React, { useMemo } from "react";

/**
 * MarkdownView — tiny inline Markdown → React renderer.
 *
 * Why inline instead of react-markdown:
 *   - react-markdown + remark-gfm pulls in ~70KB gzipped. We only need a
 *     subset (headings, bold/italic, lists, tables, hr, blockquotes,
 *     paragraphs). Adding ~70KB to the bundle for the Research tab
 *     specifically is bad ROI.
 *   - Avoiding the dependency keeps Chunk B purely additive — no
 *     package.json change, no npm install, no Render rebuild step.
 *   - The output we render is generated by our OWN prompt, so we
 *     control the Markdown surface area. We don't need a general-
 *     purpose CommonMark engine.
 *
 * Supports:
 *   # H1, ## H2, ### H3
 *   **bold**, *italic*
 *   - bulleted lists (single level)
 *   1. numbered lists (single level)
 *   | header | row | table | (GFM-style, with --- separator)
 *   --- horizontal rule
 *   > blockquote
 *   paragraphs (double-newline separated)
 *
 * Does NOT support: nested lists, code blocks, links, images. The
 * grounded AI prompt doesn't produce any of those.
 *
 * Output styling is delegated to the parent — this renders plain
 * HTML elements with ARCrypto's existing prose classes applied.
 */

/* ─── Inline formatting (bold, italic) ──────────────────────────── */

// Convert **bold** and *italic* to React nodes. Run on every text block.
// Returns an array of strings and React elements.
function applyInlineFormat(text) {
  if (!text) return [text];

  const parts = [];
  let buf = "";
  let i = 0;
  let key = 0;

  while (i < text.length) {
    // **bold** — must be paired
    if (text.startsWith("**", i)) {
      const end = text.indexOf("**", i + 2);
      if (end !== -1) {
        if (buf) {
          parts.push(buf);
          buf = "";
        }
        parts.push(
          <strong key={`b-${key++}`} className="font-semibold text-arc-ink">
            {text.slice(i + 2, end)}
          </strong>
        );
        i = end + 2;
        continue;
      }
    }
    // *italic* — single asterisk, requires non-space immediately after
    if (
      text[i] === "*" &&
      text[i + 1] &&
      text[i + 1] !== " " &&
      text[i + 1] !== "*"
    ) {
      const end = text.indexOf("*", i + 1);
      if (end !== -1 && text[end - 1] !== " ") {
        if (buf) {
          parts.push(buf);
          buf = "";
        }
        parts.push(
          <em key={`i-${key++}`} className="italic text-arc-muted">
            {text.slice(i + 1, end)}
          </em>
        );
        i = end + 1;
        continue;
      }
    }
    buf += text[i];
    i++;
  }
  if (buf) parts.push(buf);
  return parts;
}

/* ─── Table parser ─────────────────────────────────────────────── */

// GFM-style tables look like:
//   | col1 | col2 | col3 |
//   |------|------|------|
//   | r1c1 | r1c2 | r1c3 |
//   | r2c1 | r2c2 | r2c3 |
//
// Returns { headers: [...], rows: [[...], ...] } or null if the
// passed lines don't form a table.
function parseTable(lines) {
  if (lines.length < 2) return null;
  const isPipeRow = (s) => s.trim().startsWith("|") && s.includes("|");
  if (!isPipeRow(lines[0]) || !isPipeRow(lines[1])) return null;
  // Second row must be the separator: all cells are dashes (and optional colons)
  const sepCells = lines[1]
    .split("|")
    .slice(1, -1)
    .map((c) => c.trim());
  if (!sepCells.every((c) => /^:?-+:?$/.test(c))) return null;

  const splitRow = (line) =>
    line
      .split("|")
      .slice(1, -1)
      .map((c) => c.trim());

  const headers = splitRow(lines[0]);
  const rows = [];
  for (let i = 2; i < lines.length; i++) {
    if (!isPipeRow(lines[i])) break;
    rows.push(splitRow(lines[i]));
  }
  return { headers, rows, consumedLines: 2 + rows.length };
}

/* ─── Block parser ─────────────────────────────────────────────── */

// Walk the Markdown source line-by-line, emitting block-level React
// nodes. The trickiest bit is lists and tables which span multiple
// lines and need lookahead.
function parseBlocks(source) {
  const lines = source.split("\n");
  const out = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // Blank line → paragraph separator, skip
    if (!trimmed) {
      i++;
      continue;
    }

    // Horizontal rule
    if (/^-{3,}$/.test(trimmed) || /^_{3,}$/.test(trimmed)) {
      out.push(
        <hr
          key={`hr-${key++}`}
          className="my-6 border-0 border-t border-arc-line"
        />
      );
      i++;
      continue;
    }

    // Headings (#, ##, ###)
    if (trimmed.startsWith("### ")) {
      out.push(
        <h3
          key={`h3-${key++}`}
          className="text-base font-semibold text-arc-ink mt-5 mb-2"
        >
          {applyInlineFormat(trimmed.slice(4))}
        </h3>
      );
      i++;
      continue;
    }
    if (trimmed.startsWith("## ")) {
      out.push(
        <h2
          key={`h2-${key++}`}
          className="text-lg font-semibold text-arc-ink mt-6 mb-2"
        >
          {applyInlineFormat(trimmed.slice(3))}
        </h2>
      );
      i++;
      continue;
    }
    if (trimmed.startsWith("# ")) {
      out.push(
        <h1
          key={`h1-${key++}`}
          className="text-xl font-bold text-arc-ink mt-2 mb-3"
        >
          {applyInlineFormat(trimmed.slice(2))}
        </h1>
      );
      i++;
      continue;
    }

    // Blockquote (single-line — multi-line would need a different approach)
    if (trimmed.startsWith("> ")) {
      out.push(
        <blockquote
          key={`bq-${key++}`}
          className="border-l-2 border-arc-gold pl-3 my-3 text-arc-muted italic"
        >
          {applyInlineFormat(trimmed.slice(2))}
        </blockquote>
      );
      i++;
      continue;
    }

    // Table (look ahead two lines)
    if (trimmed.startsWith("|")) {
      const tbl = parseTable(lines.slice(i));
      if (tbl) {
        out.push(
          <div
            key={`tbl-${key++}`}
            className="my-4 overflow-x-auto rounded-lg border border-arc-line"
          >
            <table className="w-full text-xs">
              <thead className="bg-arc-surface/40">
                <tr>
                  {tbl.headers.map((h, hi) => (
                    <th
                      key={hi}
                      className="px-2.5 py-1.5 text-left font-mono uppercase tracking-wide text-[10px] text-arc-dim border-b border-arc-line"
                    >
                      {applyInlineFormat(h)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tbl.rows.map((r, ri) => (
                  <tr key={ri} className="border-b border-arc-line/40 last:border-b-0">
                    {r.map((c, ci) => (
                      <td
                        key={ci}
                        className="px-2.5 py-1.5 text-arc-muted whitespace-nowrap"
                      >
                        {applyInlineFormat(c)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        i += tbl.consumedLines;
        continue;
      }
    }

    // Bulleted or numbered lists (single level)
    if (/^[-*]\s+/.test(trimmed) || /^\d+\.\s+/.test(trimmed)) {
      const isOrdered = /^\d+\.\s+/.test(trimmed);
      const items = [];
      while (
        i < lines.length &&
        (/^[-*]\s+/.test(lines[i].trim()) || /^\d+\.\s+/.test(lines[i].trim()))
      ) {
        const itemText = lines[i]
          .trim()
          .replace(/^[-*]\s+/, "")
          .replace(/^\d+\.\s+/, "");
        items.push(itemText);
        i++;
      }
      const Tag = isOrdered ? "ol" : "ul";
      out.push(
        <Tag
          key={`list-${key++}`}
          className={`my-2 space-y-1 text-sm text-arc-muted ${
            isOrdered ? "list-decimal" : "list-disc"
          } pl-5`}
        >
          {items.map((it, ii) => (
            <li key={ii}>{applyInlineFormat(it)}</li>
          ))}
        </Tag>
      );
      continue;
    }

    // Default: paragraph (collect contiguous non-special lines)
    const paraLines = [line];
    let j = i + 1;
    while (j < lines.length && lines[j].trim() && !isBlockStart(lines[j].trim())) {
      paraLines.push(lines[j]);
      j++;
    }
    out.push(
      <p
        key={`p-${key++}`}
        className="text-sm text-arc-muted leading-relaxed my-2"
      >
        {applyInlineFormat(paraLines.join(" "))}
      </p>
    );
    i = j;
  }

  return out;
}

function isBlockStart(trimmed) {
  return (
    !trimmed ||
    /^#{1,3}\s/.test(trimmed) ||
    /^[-*]\s+/.test(trimmed) ||
    /^\d+\.\s+/.test(trimmed) ||
    trimmed.startsWith("|") ||
    trimmed.startsWith("> ") ||
    /^-{3,}$/.test(trimmed) ||
    /^_{3,}$/.test(trimmed)
  );
}

/* ─── Public component ─────────────────────────────────────────── */

const MarkdownView = ({ source, className = "" }) => {
  // Memoize so we don't re-parse on every parent render. Re-parses
  // only when the source string changes — fine for streaming where
  // each new chunk grows the source.
  const nodes = useMemo(() => parseBlocks(source || ""), [source]);
  return <div className={className}>{nodes}</div>;
};

export default MarkdownView;
